import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@pc/api/client';
import type { operations } from '@pc/api/schema';

/**
 * Straight off the generated contract rather than restated by hand, so a
 * change to the API surfaces here as a compile error.
 *
 * Worth knowing what the contract actually requires: only `oktaId`, `level`,
 * `orgLevel`, `levelSource` and `groups`. `email`, `displayName`, `title` and
 * `degraded` are all optional and must be rendered as absent-able.
 */
export type Identity =
  operations['getCurrentActor']['responses'][200]['content']['application/json'];

/**
 * How the actor's level was arrived at. `unresolved` is the one that matters:
 * it is the identity-side counterpart of a 403 `level-unreadable`, and means
 * the account's group membership did not map to a level at all.
 */
export type LevelSource = Identity['levelSource'];

export const meQueryKey = ['auth', 'me'] as const;

/**
 * Who the caller is.
 *
 * The server re-checks the actor against Okta on every call, so this is a live
 * authorization answer rather than a cached claim — a deactivated account stops
 * working within about a minute. That is also why it is refetched rather than
 * held forever: `staleTime` here is deliberately short.
 *
 * A 401 from this endpoint is not an error to display; it means "not logged in"
 * and the caller should render the login prompt. A 503 means Okta could not be
 * reached and must NOT be shown as a loss of access.
 */
export function useMe(): UseQueryResult<Identity> {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: async () => {
      const { data } = await api.GET('/api/auth/me');
      return data as Identity;
    },
    staleTime: 30_000,
  });
}

/**
 * Where the operator actually is, query string included.
 *
 * The path on its own loses the part that took work to build. This console puts
 * everything worth returning to in the query string — `?discordID=`, `?vmid=`,
 * `?jobId=`, and above all the audit filters, where
 * `/audit?opId=a1b2c3&outcome=DENIED` is a search somebody assembled mid-incident.
 * A session expiring at that moment and returning them to a bare `/audit`
 * silently throws the search away, at exactly the point they can least afford to
 * rebuild it from memory.
 *
 * The fragment is deliberately left out. Nothing here puts meaning in one — no
 * hash routing, no in-page anchors — and a browser never sends a fragment to
 * the server anyway, so a `returnTo` carrying one asks the open-redirect check
 * to accept a character it has no reason to expect, in exchange for nothing.
 */
function currentLocation(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** Where to send someone who needs a session, preserving where they were. */
export function loginUrl(returnTo: string = currentLocation()): string {
  // The API validates `returnTo` against open redirects, so a relative path is
  // both sufficient and the only thing it will accept. Encoding is what keeps
  // it one parameter: an unescaped `&` in the returned-to search would arrive
  // at the login endpoint as a second query parameter of its own.
  return `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}
